import { For, Show, createMemo } from 'solid-js'

import RangeFilter from '../components/measurements/RangeFilter'
import WeightChart from '../components/weight/WeightChart'
import EditIconButton from '../components/ui/EditIconButton'
import StatusMessage from '../components/ui/StatusMessage'
import TextInput from '../components/ui/TextInput'
import { useI18n } from '../i18n'
import {
   WEIGHTS_PER_PAGE,
   formatProjectionDate,
   formatRelativeDurationFromDays,
   formatWeight,
   formatWeightTimestamp,
   getBmiCategory,
   getFilteredWeightEntries,
   getHeightMeters,
   getWeightFilterLabel,
   openWeightEditModal,
   setShowWeightTarget,
   setWeightFilter,
   setWeightInputValue,
   setWeightPage,
   showWeightTarget,
   submitWeightEntry,
   weightEntries,
   weightFilter,
   weightInputValue,
   weightPage,
   weightProfile,
   weightStatus,
   type WeightEntry,
} from '../state/weightStore'
import { unitSystem } from '../state/unitSystemStore'
import { getExampleValue, getUnitLabel } from '../utils/units'

type Translate = ReturnType<typeof useI18n>

const MS_IN_DAY = 24 * 60 * 60 * 1000

type TargetProjection =
   | { kind: 'no-target' }
   | { kind: 'too-few-entries' }
   | { kind: 'flat' }
   | { kind: 'unavailable' }
   | { kind: 'diverging' }
   | { kind: 'projected'; timestamp: number; daysFromLast: number }

/**
 * Fits a straight line through the entries and projects when it crosses the
 * target weight.
 *
 * @param entries - Weight entries in chronological order.
 * @param target - The target weight, or null.
 * @returns The projection outcome.
 */
function computeTargetProjection(
   entries: WeightEntry[],
   target: number | null,
): TargetProjection {
   if (target === null) {
      return { kind: 'no-target' }
   }
   if (entries.length < 2) {
      return { kind: 'too-few-entries' }
   }
   const baseTimestamp = entries[0]!.timestamp
   const lastEntry = entries[entries.length - 1]!
   const offsets = entries.map(entry => (entry.timestamp - baseTimestamp) / MS_IN_DAY)
   const meanX = offsets.reduce((sum, value) => sum + value, 0) / offsets.length
   const meanY = entries.reduce((sum, value) => sum + value.weight, 0) / entries.length
   let numerator = 0
   let denominator = 0
   for (let index = 0; index < offsets.length; index += 1) {
      const dx = offsets[index]! - meanX
      numerator += dx * (entries[index]!.weight - meanY)
      denominator += dx * dx
   }
   const slopePerDay = denominator > 0 ? numerator / denominator : 0
   if (!Number.isFinite(slopePerDay) || Math.abs(slopePerDay) < 0.0001) {
      return { kind: 'flat' }
   }
   const intercept = meanY - slopePerDay * meanX
   const lastOffset = (lastEntry.timestamp - baseTimestamp) / MS_IN_DAY
   const daysFromLast = (target - intercept) / slopePerDay - lastOffset
   if (!Number.isFinite(daysFromLast)) {
      return { kind: 'unavailable' }
   }
   if (daysFromLast <= 0) {
      return { kind: 'diverging' }
   }
   return {
      kind: 'projected',
      daysFromLast,
      timestamp: lastEntry.timestamp + daysFromLast * MS_IN_DAY,
   }
}

/**
 * Turns a projection outcome into the sentence shown under the chart.
 *
 * @param projection - The projection outcome.
 * @param t - The translation function.
 * @returns The label.
 */
function formatTargetProjection(projection: TargetProjection, t: Translate) {
   switch (projection.kind) {
      case 'no-target':
         return t('Set a target weight to see a projected timeline.')
      case 'too-few-entries':
         return t('Add entries on more days to build a trend projection.')
      case 'flat':
         return t('Recent trend is flat, so projection is unavailable.')
      case 'diverging':
         return t('Current trend suggests you are distancing from your target weight.')
      case 'projected': {
         const duration = formatRelativeDurationFromDays(projection.daysFromLast)
         const date = formatProjectionDate(projection.timestamp)
         if (duration && date) {
            return t('Projected to reach your target on {date} ({duration}).', {
               date,
               duration,
            })
         }
         return t('Trend projection unavailable.')
      }
      default:
         return t('Trend projection unavailable.')
   }
}

/**
 * Builds the summary lines shown in the stats box for the filtered range.
 *
 * @param args - The filtered entries, latest weight, target, and height availability.
 * @param t - The translation function.
 * @returns The summary lines (empty when the range has no entries).
 */
function buildStatsSummary(
   args: {
      filtered: WeightEntry[]
      latestWeight: number | undefined
      targetWeightKg: number | null
      hasHeight: boolean
      hasAnyEntries: boolean
   },
   t: Translate,
) {
   const { filtered, latestWeight, targetWeightKg } = args
   if (filtered.length === 0) {
      return []
   }
   const items: string[] = []
   const earliest = filtered[filtered.length - 1]
   const latest = filtered[0]
   if (filtered.length >= 2 && earliest && latest) {
      const delta = latest.weight - earliest.weight
      items.push(
         `${t('Change · {label}', { label: getWeightFilterLabel(weightFilter()) })}: ${
            delta >= 0 ? '+' : ''
         }${formatWeight(delta)}`,
      )
   }
   if (!args.hasHeight && args.hasAnyEntries) {
      items.push(t('Add your height on the Account tab to unlock BMI insights.'))
   }
   if (typeof targetWeightKg !== 'number') {
      items.push(t('Set a target weight to compare progress against your goal.'))
   } else if (typeof latestWeight === 'number') {
      const delta = latestWeight - targetWeightKg
      items.push(
         delta <= 0 ?
            `${t('Goal reached')}: ${t('latest {latestWeight} vs {targetWeight} target', {
               latestWeight: formatWeight(latestWeight),
               targetWeight: formatWeight(targetWeightKg),
            })}`
         :  `${t('Latest vs goal')}: ${formatWeight(delta)}`,
      )
   }
   return items
}

/**
 * Builds the BMI card contents from the latest weight and the user's height.
 *
 * @param latestEntry - The most recent weight entry.
 * @param heightMeters - The user's height in metres, or null.
 * @param t - The translation function.
 * @returns The card contents, or null when BMI cannot be computed.
 */
function buildBmiSummary(
   latestEntry: WeightEntry | undefined,
   heightMeters: number | null,
   t: Translate,
) {
   if (!latestEntry || !heightMeters) {
      return null
   }
   const bmi = latestEntry.weight / (heightMeters * heightMeters)
   const category = getBmiCategory(bmi)
   if (!category) {
      return null
   }
   return {
      value: bmi.toFixed(1),
      weight: formatWeight(latestEntry.weight),
      label: t(category.labelKey),
      classes: category.classes,
   }
}

/**
 * Renders the BMI highlight card for the latest weight.
 *
 * @param props - The BMI summary, or null to hide the card.
 * @returns The card element.
 */
const BmiHighlight = (props: {
   summary: {
      value: string
      weight: string
      label: string
      classes: readonly string[]
   } | null
}) => {
   const t = useI18n()
   return (
      <section
         id="weight-bmi-highlight"
         class="bg-emerald-500/10 border border-emerald-400/30 rounded-xl px-4 py-3 shadow-lg shadow-emerald-900/40 flex flex-col gap-2"
         classList={{ hidden: !props.summary }}
      >
         <div class="flex items-center justify-between">
            <p class="text-xs uppercase tracking-wider text-emerald-200">
               {t('Current BMI')}
            </p>
            <span
               class={`bmi-category inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full border bg-emerald-500/20 ${
                  props.summary?.classes.join(' ') ?? ''
               }`}
            >
               {props.summary?.label}
            </span>
         </div>
         <p class="text-3xl font-black text-emerald-200 leading-tight">
            <span class="bmi-value">{props.summary?.value ?? '--'}</span>
         </p>
         <p class="text-sm text-emerald-100/80">
            {t('Based on your latest weight of')}{' '}
            <span class="bmi-weight font-semibold">{props.summary?.weight ?? '--'}</span>.
         </p>
      </section>
   )
}

/**
 * Renders the log-weight form.
 *
 * @returns The form element.
 */
const WeightEntryForm = () => {
   const t = useI18n()
   return (
      <section class="lg:h-full pb-4 space-y-3 bg-transparent lg:p-4 lg:bg-surface-raised/70 lg:rounded-xl lg:border lg:border-edge-soft">
         <form
            id="weight-form"
            class="space-y-3"
            onSubmit={event => {
               event.preventDefault()
               submitWeightEntry()
            }}
         >
            <div>
               <label
                  for="weight-input"
                  class="block mb-2 text-xl font-semibold text-ink"
               >
                  {t('Log Current Weight ({unit})', {
                     unit: getUnitLabel('mass', unitSystem()),
                  })}
               </label>
               <div class="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                  <TextInput
                     id="weight-input"
                     type="number"
                     step="0.1"
                     min="0"
                     inputmode="decimal"
                     value={weightInputValue()}
                     onInput={event => setWeightInputValue(event.currentTarget.value)}
                     placeholder={t('e.g., {value}', {
                        value: getExampleValue('mass', unitSystem()),
                     })}
                  />
                  <button
                     id="weight-submit-btn"
                     type="submit"
                     class="btn-accent px-4 py-3 text-white font-semibold rounded-lg transition sm:min-w-[80px]"
                  >
                     {t('Add')}
                  </button>
               </div>
            </div>
            <StatusMessage id="weight-status" status={weightStatus()} />
         </form>
      </section>
   )
}

/**
 * Renders the paged list of weight entries with edit/delete actions.
 *
 * @param props - The newest-first entries and the current page.
 * @returns The list element.
 */
const WeightEntryList = (props: { entries: WeightEntry[]; page: number }) => {
   const t = useI18n()
   const pageCount = createMemo(() =>
      Math.max(1, Math.ceil(props.entries.length / WEIGHTS_PER_PAGE)),
   )
   const currentPage = createMemo(() =>
      Math.min(Math.max(0, props.page), Math.max(0, pageCount() - 1)),
   )
   const pagedEntries = createMemo(() => {
      const startIndex = currentPage() * WEIGHTS_PER_PAGE
      return props.entries.slice(startIndex, startIndex + WEIGHTS_PER_PAGE)
   })
   const paginationLabel = createMemo(() => {
      const total = props.entries.length
      const start = currentPage() * WEIGHTS_PER_PAGE + 1
      const end = Math.min((currentPage() + 1) * WEIGHTS_PER_PAGE, total)
      return total > WEIGHTS_PER_PAGE ?
            t('Showing {start}-{end} of {total}', { start, end, total })
         :  ''
   })
   /**
    * Finds an entry's index in the unfiltered store list (for edit/delete).
    *
    * @param entry - The entry.
    * @returns The store index.
    */
   const storeIndexOf = (entry: WeightEntry) =>
      weightEntries().findIndex(
         candidate =>
            candidate.timestamp === entry.timestamp && candidate.weight === entry.weight,
      )
   return (
      <div>
         <div id="weight-entry-list" class="space-y-2">
            <Show
               when={pagedEntries().length > 0}
               fallback={
                  <p class="text-sm text-ink-faint">
                     {t('No entries available for this period.')}
                  </p>
               }
            >
               <For each={pagedEntries()}>
                  {entry => (
                     <div class="flex items-center justify-between gap-3 py-2 lg:px-3 lg:bg-surface-raised lg:border lg:border-edge-soft lg:rounded-lg">
                        <div>
                           <p class="text-sm font-semibold text-ink">
                              {formatWeight(entry.weight)}
                           </p>
                           <p class="text-xs text-ink-faint">
                              {formatWeightTimestamp(entry.timestamp)}
                           </p>
                        </div>
                        <EditIconButton
                           onClick={() => openWeightEditModal(storeIndexOf(entry))}
                        />
                     </div>
                  )}
               </For>
            </Show>
         </div>
         <div
            id="weight-pagination"
            class="flex items-center justify-between text-xs text-ink-faint mt-4"
         >
            <span>{paginationLabel()}</span>
            <Show when={props.entries.length > WEIGHTS_PER_PAGE}>
               <div class="flex gap-2">
                  <button
                     type="button"
                     onClick={() => setWeightPage(currentPage() - 1)}
                     disabled={currentPage() === 0}
                     class="px-2 py-1 rounded-md border border-edge bg-surface-raised text-ink-soft disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                     {t('Prev')}
                  </button>
                  <button
                     type="button"
                     onClick={() => setWeightPage(currentPage() + 1)}
                     disabled={currentPage() >= pageCount() - 1}
                     class="px-2 py-1 rounded-md border border-edge bg-surface-raised text-ink-soft disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                     {t('Next')}
                  </button>
               </div>
            </Show>
         </div>
      </div>
   )
}

/**
 * Renders the "show target" checkbox, disabled until a target weight is set.
 *
 * @param props - Whether a target weight exists.
 * @returns The toggle element.
 */
const WeightTargetToggle = (props: { enabled: boolean }) => {
   const t = useI18n()
   return (
      <div
         id="weight-target-toggle-container"
         class="flex items-center gap-2 text-sm text-ink-muted lg:flex-none"
         classList={{ 'opacity-40 pointer-events-none': !props.enabled }}
      >
         <input
            type="checkbox"
            id="weight-target-toggle"
            class="w-4 h-4 rounded border-edge bg-surface-raised text-ink focus:ring-2 focus:ring-accent-strong focus:ring-offset-0"
            checked={showWeightTarget()}
            disabled={!props.enabled}
            onChange={event => setShowWeightTarget(event.currentTarget.checked)}
         />
         <label for="weight-target-toggle" class="cursor-pointer select-none">
            {t('Show target')}
         </label>
      </div>
   )
}

/**
 * Renders the weight metric (chart, entry form, BMI, and projection) inside the
 * measurements tab.
 *
 * @returns The weight view element.
 */
const WeightView = () => {
   const t = useI18n()

   const filteredEntries = createMemo(() =>
      getFilteredWeightEntries(weightEntries(), weightFilter()),
   )
   const descendingEntries = createMemo(() =>
      [...filteredEntries()].sort((a, b) => b.timestamp - a.timestamp),
   )
   const chronologicalEntries = createMemo(() =>
      [...filteredEntries()].sort((a, b) => a.timestamp - b.timestamp),
   )
   const heightMeters = createMemo(() => getHeightMeters(weightProfile()))
   const targetWeightKg = createMemo(() => weightProfile().targetWeightKg)

   const bmiSummary = createMemo(() =>
      buildBmiSummary(weightEntries()[0], heightMeters(), t),
   )
   const statsSummary = createMemo(() =>
      buildStatsSummary(
         {
            filtered: filteredEntries(),
            latestWeight: descendingEntries()[0]?.weight,
            targetWeightKg: targetWeightKg(),
            hasHeight: Boolean(heightMeters()),
            hasAnyEntries: weightEntries().length > 0,
         },
         t,
      ),
   )
   const trendProjection = createMemo(() =>
      formatTargetProjection(
         computeTargetProjection(chronologicalEntries(), targetWeightKg()),
         t,
      ),
   )
   const hasTarget = createMemo(() => typeof targetWeightKg() === 'number')

   return (
      <div id="weight-view" class="space-y-4">
         <div class="space-y-4">
            <BmiHighlight summary={bmiSummary()} />
            <WeightEntryForm />
            <section class="lg:h-full pb-4 space-y-4 bg-transparent lg:p-4 lg:bg-surface-raised/70 lg:rounded-xl lg:border lg:border-edge-soft">
               <div class="flex items-center justify-between">
                  <h2 class="text-xl font-semibold text-ink">{t('Weight History')}</h2>
               </div>
               <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <RangeFilter
                     id="weight-filter-container"
                     value={weightFilter()}
                     onChange={setWeightFilter}
                  />
               </div>
               <div class="space-y-3">
                  <WeightChart />
                  <div class="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                     <p
                        id="weight-trend-projection"
                        class="text-xs text-ink-faint lg:text-left lg:max-w-2xl lg:flex-1 lg:pr-4"
                     >
                        {trendProjection()}
                     </p>
                     <WeightTargetToggle enabled={hasTarget()} />
                  </div>
                  <div
                     id="weight-stats"
                     class="bg-surface/60 rounded-xl border border-edge-soft p-4 text-sm text-ink-secondary space-y-2"
                  >
                     <Show
                        when={statsSummary().length > 0}
                        fallback={<p>{t('No weight entries for this range yet.')}</p>}
                     >
                        <For each={statsSummary()}>
                           {item => <div class="leading-tight">{item}</div>}
                        </For>
                     </Show>
                  </div>
               </div>
               <WeightEntryList entries={descendingEntries()} page={weightPage()} />
            </section>
         </div>
      </div>
   )
}

export default WeightView
